/**
 * 麦克风（getUserMedia）错误的统一中文描述。
 *
 * 视弹/跟弹/校准/麦克风测试四处都会请求 `getUserMedia`，各浏览器/内核报错
 * 千差万别 —— 尤其 UC 浏览器（U3/U4 内核）在「WebRTC」被禁用时，音频采集
 * 直接不可用，抛出的英文原文用户完全看不懂。这里把常见错误归类成**可操作的
 * 中文提示**，前端只负责展示，不再拼英文原文。
 *
 * 统一返回「用户能照着做」的文案，而不是开发者日志里的 `Failed to execute ...`。
 */

/** UC 浏览器（U3/U4 内核）禁用 WebRTC 时，getUserMedia 音频采集会报这个。 */
const WEBRTC_DISABLED = /webrtc is disabled|only camera is supported/i

export function describeMicError(err: unknown): string {
	const name = (err as any)?.name ?? ''
	const msg = String((err as any)?.message ?? err ?? '')

	// UC 浏览器内核限制：音频采集底层走 WebRTC，被禁后只剩摄像头可用。
	if (WEBRTC_DISABLED.test(msg)) {
		return '当前浏览器（UC 等）把麦克风采集绑定在 WebRTC 上，而 WebRTC 已被关闭，所以只能调用摄像头、拿不到声音。请在浏览器设置里开启 WebRTC，或改用 Chrome、Safari、系统自带浏览器再试。'
	}

	// 授权被拒 / 权限被禁 / 非安全上下文
	if (
		name === 'NotAllowedError' ||
		name === 'PermissionDeniedError' ||
		name === 'SecurityError'
	) {
		return '浏览器没拿到麦克风授权。请在地址栏左侧的权限图标里允许麦克风，然后重试。'
	}

	// 没找到麦克风设备 / 约束无法满足
	if (
		name === 'NotFoundError' ||
		name === 'DevicesNotFoundError' ||
		name === 'OverconstrainedError'
	) {
		return '没检测到麦克风设备。请确认麦克风已连接、未被其他应用占用，再重试。'
	}

	// 设备被占用 / 无法读取（硬件正在被别的 App 使用）
	if (name === 'NotReadableError' || name === 'TrackStartError') {
		return '麦克风正被其他应用占用。请关闭正在录音/通话的应用后再试。'
	}

	// 兜底：未知错误，保留原文便于排查
	return '无法获取麦克风：' + (msg || name || '未知错误')
}
